import { ref, watch, computed } from '@vue/composition-api'

// Notification
import { useToast } from 'vue-toastification/composition'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import axios from 'axios'
import Vue from 'vue'
import apiEndpoints from '@/constants/apiEndpoints'
import router from '../../../../router' // Verify this path remains correct

export default function useItemList() {
  const toast = useToast()
  const refItemListTable = ref(null)
  const tableColumns = [
    { key: 'item_name', sortable: true },
    { key: 'item_category', sortable: true },
    { key: 'item_code', sortable: true },
    { key: 'item_unit', sortable: true },
    { key: 'item_min_stock', sortable: true },
    { key: 'actions' },
  ]

  const perPage = ref(10)
  const totalItems = ref(0)
  const currentPage = ref(1)
  const perPageOptions = [10, 25, 50, 100]
  const searchQuery = ref('')
  const sortBy = ref('item_id')
  const isSortDirDesc = ref(true)

  const roleFilter = ref(null)
  const statusFilter = ref(null)
  const startDateFilter = ref('')
  const endDateFilter = ref('')

  // Switchable views: 'card' (default) or 'table'.
  const viewMode = ref('card')
  const items = ref([])
  const isLoadingItems = ref(false)

  const getLocalItemsCount = () => {
    if (viewMode.value !== 'table') return items.value.length
    return refItemListTable.value ? refItemListTable.value.localItems.length : 0
  }

  const dataMeta = computed(() => {
    const localItemsCount = getLocalItemsCount()
    return {
      from: perPage.value * (currentPage.value - 1) + (localItemsCount ? 1 : 0),
      to: perPage.value * (currentPage.value - 1) + localItemsCount,
      of: totalItems.value,
    }
  })

  const getOrderSort = () => {
    if (viewMode.value === 'card') return 'ASC'
    return isSortDirDesc.value ? 'DESC' : 'ASC'
  }

  const buildParams = () => ({
    limit: perPage.value,
    offset: perPage.value * (currentPage.value - 1),
    search: searchQuery.value,
    // Card view groups items A-Z, so it always asks the backend for an
    // alphabetical sort regardless of the table's own sort controls.
    order_by: viewMode.value === 'card' ? 'item_name' : sortBy.value,
    order_sort: getOrderSort(),
    date_start: startDateFilter.value,
    date_end: endDateFilter.value,
  })

  const fetchItemsData = () => {
    const userToken = Vue.$cookies.get('userToken')
    const headers = {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${userToken}`,
    }
    const params = new URLSearchParams(buildParams()).toString()

    isLoadingItems.value = true
    return axios
      .get(`${process.env.VUE_APP_API_URL}${apiEndpoints.API_GET_ITEMS}?${params}`, { headers })
      .then(response => {
        if (response.data.data !== null) {
          totalItems.value = response.data.data_count
          items.value = response.data.data
        } else {
          items.value = []
          totalItems.value = 0
        }
      })
      .catch(e => {
        if (e.message === 'Request failed with status code 401') {
          router.push({ name: 'auth-login' })
        } else {
          console.error(e)
          toast({
            component: ToastificationContent,
            props: {
              title: 'Error fetching data list',
              icon: 'AlertTriangleIcon',
              variant: 'danger',
            },
          })
        }
      })
      .finally(() => {
        isLoadingItems.value = false
      })
  }

  const refetchData = () => {
    if (viewMode.value === 'table') {
      if (refItemListTable.value) refItemListTable.value.refresh()
    } else {
      fetchItemsData()
    }
  }

  watch([
    currentPage,
    perPage,
    searchQuery,
    roleFilter,
    statusFilter,
    sortBy,
    isSortDirDesc,
    startDateFilter,
    endDateFilter,
  ], () => {
    refetchData()
  })

  watch(viewMode, () => {
    currentPage.value = 1
    if (viewMode.value === 'card') {
      fetchItemsData()
    }
  })

  const fetchItems = (ctx, callback) => {
    const userToken = Vue.$cookies.get('userToken')
    const headers = {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${userToken}`,
    }
    const params = new URLSearchParams(buildParams()).toString()

    axios
      .get(`${process.env.VUE_APP_API_URL}${apiEndpoints.API_GET_ITEMS}?${params}`, { headers })
      .then(response => {
        if (response.data.data !== null) {
          totalItems.value = response.data.data_count
          callback(response.data.data)
        } else {
          toast({
            component: ToastificationContent,
            props: {
              title: 'Error Fetching Items list',
              icon: 'AlertTriangleIcon',
              variant: 'danger',
            },
          })
        }
      })
      .catch(e => {
        if (e.message === 'Request failed with status code 401') {
          router.push({ name: 'auth-login' })
        } else {
          console.error(e)
          toast({
            component: ToastificationContent,
            props: {
              title: 'Error fetching data list',
              icon: 'AlertTriangleIcon',
              variant: 'danger',
            },
          })
        }
      })
  }

  // *===============================================---*
  // *--------- UI ---------------------------------------*
  // *===============================================---*

  const resolveUserRoleVariant = role => {
    if (role === 'subscriber') return 'primary'
    if (role === 'author') return 'warning'
    if (role === 'maintainer') return 'success'
    if (role === 'editor') return 'info'
    if (role === 'super-admin') return 'danger'
    return 'primary'
  }

  const resolveUserRoleIcon = role => {
    if (role === 'subscriber') return 'UserIcon'
    if (role === 'author') return 'SettingsIcon'
    if (role === 'maintainer') return 'DatabaseIcon'
    if (role === 'editor') return 'Edit2Icon'
    if (role === 'super-admin') return 'ServerIcon'
    return 'UserIcon'
  }

  const resolveUserStatusVariant = status => {
    if (status === 'pending') return 'warning'
    if (status === 'active') return 'success'
    if (status === 'inactive') return 'secondary'
    return 'primary'
  }

  return {
    fetchItems,
    fetchItemsData,
    tableColumns,
    perPage,
    currentPage,
    totalItems,
    dataMeta,
    perPageOptions,
    searchQuery,
    sortBy,
    isSortDirDesc,
    refItemListTable,
    resolveUserRoleVariant,
    resolveUserRoleIcon,
    resolveUserStatusVariant,
    refetchData,

    // Switchable views
    viewMode,
    items,
    isLoadingItems,

    // Extra Filters
    roleFilter,
    statusFilter,
    startDateFilter,
    endDateFilter,
  }
}